import { inputCssNoMy } from "../reuseables/native.comp";
import "react-datepicker/dist/react-datepicker.css";

import { UseMutationResult, useQuery } from "@tanstack/react-query";
import ReactDatePicker from "react-datepicker";
import { Controller, UseFormReturn, useForm, useWatch } from "react-hook-form";

import { getSingleCategory } from "../../core/category.core";
import { useTranslation } from "../../core/translation.core";
import useSingleProductQuery from "../../hooks/useSingleProductQuery.hook";
import CloseIcon from "../../icons/iconClose.comp";
import { DiscountFormData, DiscountItemFormData, DiscountItemType, ItemsScope } from "../../types/discount";
import GenericFormEditor from "../form/genericFormEditor";
import LocalePs from "../locale/localePs.core";
import ProductSearchInput from "../product/productSearchInput.core";
import CategoryInput from "../reuseables/categoryInput.comp";
import { FancyTextInput } from "../reuseables/form.comp";
import { selectCss } from "../reuseables/native.comp";
import { EditorSection, Title } from "../reuseables/editor.comp";
import PlacementRestrictionForm from "../shipSetting/placementRestriction.comp";
import ProductRestrictionForm from "../shipSetting/productRestriction.comp";
import CategoryRestrictionForm from "../shipSetting/categoryRestriction.comp";
import ShipOptionRestrictionForm from "../shipSetting/shipOptionRestriction.comp";
import ScheduleDiscountBuyXForm from "./scheduleDiscountBuyXForm.comp";

interface Props {
  methods: UseFormReturn<DiscountFormData, any>;
  mutation: UseMutationResult<void, Error, DiscountFormData, unknown>;
  btnText: string;
}

export default function ScheduleDiscountEditor({ methods, mutation, btnText }: Props) {
  const { t, msg } = useTranslation();

  return (
    <GenericFormEditor methods={methods} mutation={mutation} btnText={btnText}>
      <EditorSection>
        <Title>{t(msg.product.generalInfo)}</Title>
        <div>
          <label>
            {t(msg.addOnDiscount.name)}
            <FancyTextInput register={() => methods.register("name")} />
          </label>
        </div>
      </EditorSection>

      <ScheduleDiscountBuyXForm methods={methods} />

      <EditorSection>
        <Title>{t(msg.ship.placementRestriction)}</Title>
        <PlacementRestrictionForm<DiscountFormData>
          methods={methods}
          typeFieldName="placementRestriction.type"
          eshopFieldName="placementRestriction.restrictToEshop"
          retailIdsFieldName="placementRestriction.restrictToRetailIds"
        />
      </EditorSection>

      <EditorSection>
        <Title>Product Restriction</Title>
        <ProductRestrictionForm<DiscountFormData>
          methods={methods}
          typeFieldName="productRestriction.type"
          idsFieldName="productRestriction.restrictToIds"
        />
      </EditorSection>

      <EditorSection>
        <Title>Category Restriction</Title>
        <CategoryRestrictionForm<DiscountFormData>
          methods={methods}
          typeFieldName="categoryRestriction.type"
          idsFieldName="categoryRestriction.restrictToIds"
        />
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.ship.shipOptionRestriction)}</Title>
        <ShipOptionRestrictionForm<DiscountFormData>
          methods={methods}
          typeFieldName="shipOptionRestriction.type"
          idsFieldName="shipOptionRestriction.restrictToIds"
        />
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.discount.schedule)}</Title>
        <div>
          <label className="flex mb-2 items-center" id="schedule-start">
            <span className="mr-2 basis-24">{t(msg.discount.startDate)}:</span>
            <Controller
              control={methods.control}
              name="schedule.start"
              render={({ field: { onChange, value } }) => (
                <ReactDatePicker
                  // minDate={new Date()}
                  showTimeSelect
                  className={`${inputCssNoMy} min-w-[250px]`}
                  onChange={(value) => {
                    onChange(value);
                  }}
                  selected={value}
                  dateFormat="MMMM d, yyyy h:mm aa"
                />
              )}
            />
          </label>

          <label className="flex items-center" id="schedule-end">
            <span className="mr-2 basis-24">{t(msg.discount.endDate)}:</span>
            <Controller
              control={methods.control}
              name="schedule.end"
              render={({ field: { onChange, value } }) => (
                <ReactDatePicker
                  minDate={new Date()}
                  showTimeSelect
                  className={`${inputCssNoMy} min-w-[250px]`}
                  onChange={(v: Date) => onChange(v)}
                  selected={value}
                  dateFormat="MMMM d, yyyy h:mm aa"
                />
              )}
            />
          </label>
        </div>
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.discount.byCategories)}</Title>
        <div>
          <DiscountItems methods={methods} itemsScope="cats" />
        </div>
      </EditorSection>
      <EditorSection>
        <Title>{t(msg.discount.byProducts)}</Title>
        <div>
          <DiscountItems methods={methods} itemsScope="products" />
        </div>
      </EditorSection>
    </GenericFormEditor>
  );
}

const Selector = ({
  methods,
  label,
  fieldName,
  options,
}: {
  methods: any;
  label: string;
  fieldName: string;
  options: { name: string; value: string }[];
}) => {
  return (
    <div className="mt-4 mb-4">
      <label className="block">{label}</label>
      <select className={selectCss} {...methods.register(fieldName)}>
        {options.map((o) => (
          <option key={o.name} value={o.value}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
};

const DiscountItems = ({ methods, itemsScope }: { methods: UseFormReturn<DiscountFormData, any>; itemsScope: ItemsScope }) => {
  function addItem(itemId: string, type: DiscountItemType) {
    let discountItems = methods.getValues("discountItems");

    // find it
    if (!discountItems) {
      discountItems = [];
    }

    const found = discountItems.find((p) => p.productId === itemId || p.categoryId === itemId);
    if (found) return;

    discountItems.push({
      discountType: "percentage",
      discountPercentage: null,
      discountPrice: null,
      maxQuantity: null,
      itemType: type,
      scope: itemsScope,
      ...(type === "product" ? { productId: itemId } : {}),
      ...(type === "category" ? { categoryId: itemId } : {}),
    });

    methods.setValue("discountItems", discountItems);
  }

  const handleCategories = (data: string[]) => {
    for (const c of data) {
      addItem(c, "category");
    }
  };

  return (
    <>
      {itemsScope === "cats" && (
        <div id="discount-categories">
          <DiscountCategoryInput handleSubmit={handleCategories} />
        </div>
      )}
      {itemsScope === "products" && (
        <div id="discount-products">
          <ProductSearchInput addProduct={(productId) => addItem(productId, "product")} />
        </div>
      )}
      <DiscountItemsTable methods={methods} itemsScope={itemsScope} />
    </>
  );
};

const DiscountItemsTable = ({
  methods,
  itemsScope,
}: {
  methods: UseFormReturn<DiscountFormData, any>;
  itemsScope: ItemsScope;
}) => {
  const { t, msg } = useTranslation();

  const discountItems = useWatch({
    control: methods.control,
    name: "discountItems",
  });

  if (!discountItems) return null;

  const items = discountItems.filter((p) => p.scope === itemsScope);
  if (items.length === 0) return null;

  return (
    <div id="discount-items-table" className="col-span-2">
      <div className="mt-4 grid grid-cols-[1fr_1fr_30px] lg:grid-cols-[1fr_1fr_100px_150px_30px] gap-2 pb-2 items-center">
        <div className="font-medium">Item</div>
        <div className="font-medium col-span-2 lg:col-span-1">{t(msg.discount.discountType)}</div>
        <div className="font-medium">Value</div>
        <div className="font-medium">{t(msg.discount.maxQuantity)}</div>
        <div></div>
      </div>
      {items.map((p, idx) => (
        <DiscountItemRow key={p.productId ?? p.categoryId} idx={idx} discountItem={p} methods={methods} itemsScope={itemsScope} />
      ))}
    </div>
  );
};

const DiscountItemRow = ({
  idx,
  discountItem,
  methods,
  itemsScope,
}: {
  idx: number;
  discountItem: DiscountItemFormData;
  methods: UseFormReturn<DiscountFormData, any>;
  itemsScope: ItemsScope;
}) => {
  const { t, msg } = useTranslation();
  const { data: product, isFetched: productIsFetched } = useSingleProductQuery(discountItem.productId);

  const { data: cateogry, isFetched: categoryIsFetched } = useQuery({
    queryKey: ["category", discountItem.categoryId],
    queryFn: (context) => getSingleCategory(context.queryKey[1]!),
    enabled: Boolean(discountItem.categoryId),
  });

  if (!productIsFetched && !categoryIsFetched) return null;

  function deleteItem(id: string) {
    const discountItems = methods.getValues("discountItems") ?? [];
    const updatedArray = discountItems.filter((j) => j.productId !== id && j.categoryId !== id);
    methods.setValue("discountItems", updatedArray, { shouldValidate: false });
  }

  const discountType = methods.watch(`discountItems.${idx}.discountType`);

  if (discountItem.scope !== itemsScope) return null;

  return (
    <>
      <div className="grid grid-cols-[1fr_1fr_30px] lg:grid-cols-[1fr_1fr_100px_150px_30px] gap-2 border-t-[1px] py-2 items-center">
        <div>
          <LocalePs value={product?.name || cateogry?.name} />
        </div>
        <div className="flex items-center col-span-2 lg:col-span-1">
          <select className={selectCss} {...methods.register(`discountItems.${idx}.discountType`)}>
            <option value="fixed">{t(msg.discount.priceAfterDiscount)}</option>
            <option value="percentage">{t(msg.discount.percentage)}</option>
          </select>
        </div>
        <div className="flex items-center">
          {discountType === "fixed" && (
            <FancyTextInput
              register={() =>
                methods.register(`discountItems.${idx}.discountPrice`, {
                  valueAsNumber: true,
                })
              }
            />
          )}
          {discountType === "percentage" && (
            <FancyTextInput
              register={() =>
                methods.register(`discountItems.${idx}.discountPercentage`, {
                  valueAsNumber: true,
                })
              }
            />
          )}
        </div>
        <div className="flex items-center">
          <FancyTextInput
            register={() =>
              methods.register(`discountItems.${idx}.maxQuantity`, {
                valueAsNumber: true,
                min: { value: 1, message: "Must be at least 1" },
              })
            }
          />
        </div>
        <div className="flex items-center justify-center">
          <button onClick={() => deleteItem(discountItem.scope === "cats" ? discountItem.categoryId! : discountItem.productId!)}>
            <CloseIcon />
          </button>
        </div>
      </div>
      {(methods.formState.errors?.discountItems?.[idx]?.discountPrice?.message ||
        methods.formState.errors?.discountItems?.[idx]?.discountPercentage?.message ||
        methods.formState.errors?.discountItems?.[idx]?.maxQuantity?.message) && (
        <div className="grid grid-cols-[1fr_1fr_100px_150px_30px] gap-2">
          <div></div>
          <div className="col-span-2 lg:col-span-1"></div>
          <div className="text-red-500 text-xs">
            {methods.formState.errors?.discountItems?.[idx]?.discountPrice?.message ||
              methods.formState.errors?.discountItems?.[idx]?.discountPercentage?.message}
          </div>
          <div className="text-red-500 text-xs">{methods.formState.errors?.discountItems?.[idx]?.maxQuantity?.message}</div>
          <div></div>
        </div>
      )}
    </>
  );
};

const DiscountCategoryInput = ({ handleSubmit }: { handleSubmit: (data: string[]) => void }) => {
  const { t, msg } = useTranslation();
  const categoryMethods = useForm();

  const handleCatSubmt = (data: string[]) => {
    handleSubmit(data);
  };

  return (
    <div>
      <CategoryInput register={() => categoryMethods.register("cat")} />
      <button
        type="button"
        onClick={() => handleCatSubmt(categoryMethods.getValues("cat"))}
        className="text-white bg-her-dodgerblue px-2 py-1 rounded outline-none cursor-pointer mt-2"
      >
        {t(msg.form.select)}
      </button>
    </div>
  );
};
